import { useEffect, useRef, useState } from 'react';
import { Badge, Button } from '@staff-app/ui';
import { AlertTriangle, ExternalLink, RefreshCw, Truck } from 'lucide-react';
import {
  INTL_DEVICE_LABELS,
  formatMinor,
  isShopifyClaimStuck,
  isShopifyRoute,
  type IntlAllPricing,
  type IntlAllRequest,
  type IntlAllShipment,
} from '@staff-app/shared-types';
import { carrierTrackingUrl } from '@/features/intl-requests/carrier-tracking';
import { ClearStuckOrderDialog } from './ClearStuckOrderDialog';
import {
  useCancelIntlAllMcfOrder,
  useCanFulfilAir,
  useIsRootAir,
  usePlaceIntlAllShopifyOrder,
  useIntlAllInventory,
  useRefreshIntlAllTracking,
  useReleaseIntlAllMcfOrder,
  useSubmitIntlAllMcfOrder,
} from '../hooks/useIntlAllRequests';

export const isPaid = (r: Pick<IntlAllRequest, 'invoicePaidAt' | 'invoiceStatus'>) =>
  Boolean(r.invoicePaidAt) || r.invoiceStatus === 'paid';
/** Paid, or root approved it to ship before payment — what opens the order buttons. */
export const mayShip = (r: Pick<IntlAllRequest, 'invoicePaidAt' | 'invoiceStatus' | 'fulfilApprovedAt'>) =>
  isPaid(r) || Boolean(r.fulfilApprovedAt);
export const isWrittenOff = (r: Pick<IntlAllRequest, 'invoiceStatus'>) =>
  r.invoiceStatus === 'void' || r.invoiceStatus === 'uncollectible';

function unitsText(s: IntlAllShipment): string {
  return s.units.map((u) => `${u.quantity} × ${INTL_DEVICE_LABELS[u.deviceModel] ?? u.deviceModel}`).join(', ');
}

/**
 * SHIPMENTS — one card per route the request was split across, with that route's action.
 *
 * MCF: the poller creates the Amazon order on HOLD once paid; a human releases it. The button here
 * is the same create, for when the poller has not run yet.
 * China / Houston: "Create Shopify order" places a REAL $0 order (no Shopify test mode) and moves
 * it to the route's location. CREATING IS NOT SHIPPING — the label is still made in the Shopify
 * admin, which is what the link is for (the RMA lane's wording, for the same reason).
 *
 * The buttons follow the server's tiers (ROOT + FULFILLMENT; Clear stuck order ROOT only) and its
 * gates (paid, not written off). Hiding them is UX; the resolver refuses either way.
 */
export function ShipmentsPanel({ request, pricing }: { request: IntlAllRequest; pricing?: IntlAllPricing | null }) {
  const canFulfil = useCanFulfilAir();
  const isRoot = useIsRootAir();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [stuck, setStuck] = useState<IntlAllShipment | null>(null);
  const [submitMcf, mcfSubmit] = useSubmitIntlAllMcfOrder();
  const [releaseMcf, mcfRelease] = useReleaseIntlAllMcfOrder();
  const [cancelMcf, mcfCancel] = useCancelIntlAllMcfOrder();
  const [placeShopify, shopify] = usePlaceIntlAllShopifyOrder();
  const [refreshTracking, tracking] = useRefreshIntlAllTracking();

  const paid = mayShip(request);
  const writtenOff = isWrittenOff(request);
  const mayAct = canFulfil && paid && !writtenOff;
  const vars = { variables: { requestId: request.id } };

  // China / Houston stock from the ops inventory sheet (Paul, 2026-10-29). A shortfall WARNS: the create asks for a confirm and
  // goes ahead with `allowShortfall`, because the sheet is kept by hand and a paid order must not
  // be stranded by it. The server checks again at the moment of the create.
  const hasShopifyShipment = request.shipments.some((s) => isShopifyRoute(s.route) && !s.shopifyOrderId);
  const { data: invData } = useIntlAllInventory(request.id, { skip: !mayAct || !hasShopifyShipment });
  const shortfallOf = (ship: IntlAllShipment): string[] =>
    ship.units.flatMap((u) => {
      const level = invData?.intlAllInventory.find((l) => l.route === ship.route && l.deviceModel === u.deviceModel);
      return level?.onHand != null && u.quantity > level.onHand
        ? [`${INTL_DEVICE_LABELS[u.deviceModel] ?? u.deviceModel}: ${level.onHand} on hand, this order needs ${u.quantity}`]
        : [];
    });
  const stockLine = (ship: IntlAllShipment): string | null => {
    const parts = ship.units.flatMap((u) => {
      const level = invData?.intlAllInventory.find((l) => l.route === ship.route && l.deviceModel === u.deviceModel);
      if (!level) return [];
      return [`${INTL_DEVICE_LABELS[u.deviceModel] ?? u.deviceModel} ${level.onHand == null ? (level.error ? 'unavailable' : 'not on sheet') : level.onHand}`];
    });
    return parts.length ? `Stock at ${ship.routeLabel}: ${parts.join(', ')}` : null;
  };
  const place = (route: string, allowShortfall: boolean) =>
    placeShopify({ variables: { requestId: request.id, route, allowShortfall } }).catch((e: Error) => {
      // The count moved between opening the drawer and pressing Create: ask, as for a known shortfall.
      if (/Not enough stock/.test(e.message)) setConfirming(route);
    });

  // Opening the drawer reads tracking for any ordered shipment still missing it (Paul, 2026-10-28):
  // otherwise a label made in Shopify only appeared after someone pressed Refresh tracking or the
  // twice-daily poll ran. Once per request opened; nothing to read means no call at all.
  const awaitingTracking = request.shipments.some((s) => s.ordered && !s.trackingNumber);
  const autoRefreshed = useRef<number | null>(null);
  useEffect(() => {
    if (!awaitingTracking || autoRefreshed.current === request.id) return;
    autoRefreshed.current = request.id;
    refreshTracking({ variables: { requestId: request.id } }).catch(() => {
      // A failed background read is not the operator's error; the button still works.
    });
  }, [awaitingTracking, request.id, refreshTracking]);
  const swallow = () => {
    // Surfaced from the mutation's `error` below.
  };
  const actionError =
    mcfSubmit.error ?? mcfRelease.error ?? mcfCancel.error ?? shopify.error ?? tracking.error ?? null;

  if (!request.shipments.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="air-shipments-empty">
        {'No shipments yet — they are created when fulfilment routes the request.'}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="air-shipments">
      {!paid && !writtenOff ? (
        <p className="text-xs text-muted-foreground" data-testid="air-shipments-unpaid">
          {'Orders can be placed once the invoice is paid, or once a root user approves shipping before payment.'}
        </p>
      ) : null}
      {writtenOff ? (
        <p className="text-xs text-destructive" data-testid="air-shipments-written-off">
          {`The invoice is ${request.invoiceStatus}, so nothing may ship.`}
        </p>
      ) : null}

      {request.shipments.map((s) => {
        const shopifyRoute = isShopifyRoute(s.route);
        const isStuck = isShopifyClaimStuck(s);
        // The api builds it from the store AIR ships through (a test store outside prod).
        const adminUrl = s.shopifyAdminUrl ?? null;
        const trackUrl = s.trackingUrl ?? carrierTrackingUrl(s.trackingCarrier, s.trackingNumber, s.mcfMarketplace);
        return (
          <div key={s.route} className="rounded-md border p-3 text-sm" data-testid={`air-shipment-${s.route}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="font-medium">{s.routeLabel}</span>
                <span className="text-muted-foreground">{` · ${unitsText(s)}`}</span>
              </div>
              <span className="tabular-nums text-muted-foreground">
                {freightText(s, pricing)}
              </span>
            </div>

            {/* ---- MCF ---- */}
            {!shopifyRoute ? (
              <div className="mt-2 space-y-2">
                {s.mcfOrderId ? (
                  <p data-testid={`air-mcf-status-${s.route}`}>
                    {`Amazon order ${s.mcfOrderId} · `}
                    {s.mcfCancelledAt
                      ? 'cancelled'
                      : s.mcfReleasedAt
                        ? `released${s.mcfReleasedBy ? ` by ${s.mcfReleasedBy}` : ''}`
                        : 'on hold — release it to ship'}
                    {s.mcfStatus ? ` (${s.mcfStatus})` : ''}
                  </p>
                ) : null}
                {s.mcfError ? <p className="text-destructive">{s.mcfError}</p> : null}
                {mayAct ? (
                  <div className="flex flex-wrap gap-2">
                    {!s.mcfOrderId ? (
                      <Button
                        size="sm"
                        onClick={() => submitMcf(vars).catch(swallow)}
                        disabled={mcfSubmit.loading}
                        data-testid="air-mcf-submit"
                      >
                        {'Create Amazon order (on hold)'}
                      </Button>
                    ) : null}
                    {s.mcfOrderId && !s.mcfReleasedAt && !s.mcfCancelledAt ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => releaseMcf(vars).catch(swallow)}
                          disabled={mcfRelease.loading}
                          data-testid="air-mcf-release"
                        >
                          {'Release to ship'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelMcf(vars).catch(swallow)}
                          disabled={mcfCancel.loading}
                          data-testid="air-mcf-cancel"
                        >
                          {'Cancel Amazon order'}
                        </Button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* ---- China / Houston (Shopify) ---- */}
            {shopifyRoute ? (
              <div className="mt-2 space-y-2">
                {s.shopifyOrderId ? (
                  <div className="space-y-1" data-testid={`air-shopify-order-${s.route}`}>
                    <p>
                      {`Shopify order ${s.shopifyOrderName ?? s.shopifyOrderId}`}
                      {s.shopifyOrderedBy ? ` · created by ${s.shopifyOrderedBy}` : ''}
                    </p>
                    {!s.trackingNumber ? (
                      <p className="text-xs text-muted-foreground">
                        {'Not shipped yet — creating the order is not shipping it. Make the shipping label in Shopify.'}
                      </p>
                    ) : null}
                    {adminUrl ? (
                      <Button size="sm" variant="outline" asChild className="gap-1">
                        <a href={adminUrl} target="_blank" rel="noreferrer" data-testid={`air-shopify-open-${s.route}`}>
                          <Truck className="h-4 w-4" />
                          {'Open in Shopify'}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                {isStuck ? (
                  <div className="flex flex-wrap items-center gap-2" data-testid={`air-shopify-stuck-${s.route}`}>
                    <Badge variant="warning" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {'Creating… never recorded'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {`Started${s.shopifyOrderedBy ? ` by ${s.shopifyOrderedBy}` : ''}. Check Shopify for tag air-${request.id}.`}
                    </span>
                    {isRoot ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setStuck(s)}
                        data-testid={`air-clear-stuck-${s.route}`}
                      >
                        {'Clear stuck order'}
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                {s.shopifyError ? <p className="text-destructive">{s.shopifyError}</p> : null}

                {mayAct && !s.shopifyOrderId && !isStuck ? (
                  confirming === s.route ? (
                    <div className="space-y-2 rounded-md border border-warning/40 p-2" data-testid={`air-shopify-confirm-${s.route}`}>
                      {shortfallOf(s).length || /Not enough stock/.test(shopify.error?.message ?? '') ? (
                        <p className="text-xs font-medium text-amber-600" data-testid={`air-shopify-shortfall-${s.route}`}>
                          {`Not enough stock at ${s.routeLabel}`}
                          {shortfallOf(s).length ? ` — ${shortfallOf(s).join('; ')}` : ''}
                          {'. Create the order anyway?'}
                        </p>
                      ) : null}
                      {request.shopifyLiveStore === false ? null : (
                        <p className="text-xs">
                          {`This creates a REAL $0 order in Shopify for ${unitsText(s)} at ${s.routeLabel}. `}
                          {'There is no test mode — the only undo is cancelling it in the Shopify admin.'}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setConfirming(null);
                            const allow = shortfallOf(s).length > 0 || /Not enough stock/.test(shopify.error?.message ?? '');
                            void place(s.route, allow);
                          }}
                          disabled={shopify.loading}
                          data-testid={`air-shopify-confirm-yes-${s.route}`}
                        >
                          {'Create it'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                          {'Cancel'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      // Ask only on the REAL store. Sandbox / qa ship through a test store (Paul,
                      // 2026-10-28), where an order costs nothing and the warning was noise.
                      // A shortfall always asks, test store or not: it is the stock, not the store, in question.
                      onClick={() =>
                        request.shopifyLiveStore === false && !shortfallOf(s).length
                          ? void place(s.route, false)
                          : setConfirming(s.route)
                      }
                      disabled={shopify.loading}
                      data-testid={`air-shopify-create-${s.route}`}
                    >
                      {shopify.loading ? 'Creating…' : 'Create Shopify order'}
                    </Button>
                  )
                ) : null}
                {mayAct && !s.shopifyOrderId && stockLine(s) ? (
                  <p
                    className={`text-xs ${shortfallOf(s).length ? 'text-amber-600' : 'text-muted-foreground'}`}
                    data-testid={`air-shopify-stock-${s.route}`}
                  >
                    {stockLine(s)}
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* ---- Tracking ---- */}
            {s.trackingNumber ? (
              <p className="mt-2" data-testid={`air-tracking-${s.route}`}>
                {'Tracking '}
                {trackUrl ? (
                  <a href={trackUrl} target="_blank" rel="noreferrer" className="font-mono underline">
                    {s.trackingNumber}
                  </a>
                ) : (
                  <span className="font-mono">{s.trackingNumber}</span>
                )}
                {s.trackingCarrier ? ` · ${s.trackingCarrier}` : ''}
              </p>
            ) : s.ordered ? (
              <p className="mt-2 text-xs text-muted-foreground" data-testid={`air-tracking-${s.route}`}>
                {'Not shipped — no tracking yet.'}
              </p>
            ) : null}
          </div>
        );
      })}

      {request.shipments.some((s) => s.ordered) ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => refreshTracking(vars).catch(swallow)}
          disabled={tracking.loading}
          data-testid="air-tracking-refresh"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${tracking.loading ? 'animate-spin' : ''}`} />
          {'Refresh tracking'}
        </Button>
      ) : null}

      {actionError ? (
        <p className="text-sm text-destructive" data-testid="air-shipments-error">
          {actionError.message}
        </p>
      ) : null}

      {stuck ? (
        <ClearStuckOrderDialog
          request={request}
          shipment={stuck}
          open
          onOpenChange={(o) => {
            if (!o) setStuck(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The freight this shipment carries: what the quote / invoice billed once it exists, else what the
 * Price card shows now (the system figure, or a saved edit — Paul, 2026-10-28), so the two sections
 * never disagree.
 */
export function freightText(s: IntlAllShipment, pricing?: IntlAllPricing | null): string {
  if (s.freightMinor != null && s.freightCurrency) return `Freight ${formatMinor(s.freightMinor, s.freightCurrency)}`;
  const priced = pricing?.shipments.find((p) => p.route === s.route);
  if (priced?.freightMinor != null && pricing) {
    return `Freight ${formatMinor(priced.freightMinor, pricing.currency)}${priced.overriddenBy ? ' (edited)' : ''}`;
  }
  if (s.freightOverrideMinor != null && s.freightOverrideCurrency) {
    return `Freight ${formatMinor(s.freightOverrideMinor, s.freightOverrideCurrency)} (edited)`;
  }
  return 'Freight not priced yet';
}
